import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileUp, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("vlx_admin_token") || ""}` });

export const ImportPdfDialog = ({ defaultKind = "invoice", onClose, onImported }) => {
  const [kind, setKind] = useState(defaultKind);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, stage: "" });
  const inputRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const run = async () => {
    if (!file) { toast.error("Choose a PDF first"); return; }
    setBusy(true);
    setProgress({ pct: 0, stage: "Uploading PDF" });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: job } = await axios.post(`${API}/import-pdf`, fd, {
        headers: { ...auth(), "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress({ pct: Math.round((e.loaded / e.total) * 5), stage: "Uploading PDF" }),
      });
      let last = 5;
      while (alive.current) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data } = await axios.get(`${API}/import-pdf/${job.job_id}`, { headers: auth() });
        if (data.status === "error") throw new Error(data.error);
        if (data.status === "done") {
          setProgress({ pct: 100, stage: "Done" });
          const d = data.result;
          toast.success(`Read ${d.line_items.length} line item${d.line_items.length === 1 ? "" : "s"}${d.images_attached ? ` · ${d.images_attached} photo${d.images_attached === 1 ? "" : "s"}` : ""}${d.source_number ? ` from ${d.source_number}` : ""}`);
          onImported(kind, d);
          return;
        }
        // creep toward the next milestone so the bar never looks frozen during the long AI read
        last = Math.max(data.progress || 0, Math.min(last + 1, (data.progress || 0) + 40, 68));
        setProgress({ pct: last, stage: data.stage || "Working" });
      }
    } catch (e) {
      const d = e.response?.data?.detail || e.message;
      toast.error(typeof d === "string" && d ? d : "Could not read this PDF");
      if (alive.current) setBusy(false);
    }
  };

  return (
    <div data-testid="import-pdf-dialog" className="fixed inset-0 z-[2000] bg-[#0a1a17]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-cream shadow-2xl">
        <div className="bg-[#0f2420] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]">AI reads the PDF</div>
            <h3 className="font-cormorant italic text-2xl">Import from PDF</h3>
          </div>
          <button data-testid="import-pdf-close" onClick={onClose} aria-label="Close" className="text-[#e2b880] hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <button
            type="button"
            data-testid="import-pdf-dropzone"
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-[#c99658]/60 hover:border-[#7a5b2f] bg-white p-6 flex flex-col items-center gap-2 text-center transition-colors"
          >
            <FileUp size={26} className="text-[#7a5b2f]" />
            <span data-testid="import-pdf-filename" className="text-sm text-[#0f2420]">{file ? file.name : "Choose an estimate or quote PDF"}</span>
            <span className="text-[11px] text-neutral-500">Text or scanned · up to 15 MB</span>
            <input ref={inputRef} data-testid="import-pdf-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </button>
          <div>
            <div className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-2">Open as</div>
            <div className="grid grid-cols-2 gap-2">
              {[["estimate", "Estimate"], ["invoice", "Invoice"]].map(([k, label]) => (
                <button key={k} type="button" data-testid={`import-kind-${k}`} onClick={() => setKind(k)}
                  className={`py-2 text-[11px] tracking-[0.25em] uppercase font-raleway border transition-colors ${kind === k ? "bg-[#0f2420] text-white border-[#0f2420]" : "bg-white border-neutral-300 text-neutral-600 hover:border-[#7a5b2f]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[11.5px] text-neutral-600 leading-snug">Prices are kept exactly as printed — you can review and change anything before saving.</p>
          {busy && (
            <div data-testid="import-pdf-progress" className="space-y-2">
              <div className="flex justify-between text-[11px] font-raleway tracking-wide text-[#0f2420]">
                <span data-testid="import-pdf-stage">{progress.stage}</span>
                <span data-testid="import-pdf-percent" className="tabular-nums text-[#7a5b2f]">{progress.pct}%</span>
              </div>
              <div className="h-1.5 w-full bg-[#0f2420]/10 overflow-hidden">
                <div className="h-full bg-[#c99658] transition-[width] duration-700 ease-out" style={{ width: `${progress.pct}%` }} />
              </div>
              <p className="text-[11px] text-neutral-500">Runs in the background — big quotes take about a minute. You can keep this window open.</p>
            </div>
          )}
        </div>
        <div className="bg-[#0f2420]/5 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[11px] tracking-[0.3em] uppercase font-raleway text-neutral-600 hover:text-[#0f2420]">Cancel</button>
          <button data-testid="import-pdf-submit" disabled={busy || !file} onClick={run}
            className="px-6 py-2 bg-[#c99658] text-[#0f2420] text-[11px] tracking-[0.3em] uppercase font-raleway hover:bg-[#7a5b2f] hover:text-white disabled:opacity-50">
            {busy ? "Reading PDF…" : "Read & open"}
          </button>
        </div>
      </div>
    </div>
  );
};
